// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the scratchpad mark, a notes page with a pen in its bottom-right corner (2026-09).
// Drawn on Tabler's 24px grid and stroke so it sits with the other icons; takes the same
// props as the icons in Icons.tsx (size, color, weight as stroke width, mirrored, alt).

import type {Icon, IconProps, IconWeight} from '@app/features/ui/icons/Icons';
import {IconContext} from '@app/features/ui/icons/Icons';
import {createReactComponent} from '@tabler/icons-react';
import type {CSSProperties} from 'react';
import {forwardRef, useContext} from 'react';

const STROKE: Record<IconWeight, number> = {thin: 1, light: 1.25, regular: 1.75, bold: 2.25, fill: 2.25, duotone: 1.75};

const IconNotesPen = createReactComponent('outline', 'notes-pen', 'NotesPen', [
	['path', {d: 'M12 21h-5a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6', key: 'page'}],
	['path', {d: 'M9 7h6', key: 'line1'}],
	['path', {d: 'M9 11h6', key: 'line2'}],
	['path', {d: 'M9 15h3', key: 'line3'}],
	['path', {d: 'M13.44 22.4h2.24l5.88 -5.88a1.584 1.584 0 1 0 -2.24 -2.24l-5.88 5.88v2.24', key: 'pen'}],
	['path', {d: 'M18.76 14.84l2.24 2.24', key: 'pen-tip'}],
]);

export const ScratchpadIcon: Icon = forwardRef<SVGSVGElement, IconProps>((props, ref) => {
	const context = useContext(IconContext);
	const {size, color, weight, mirrored, style, alt, ...rest} = {...context, ...props};
	const mirroredStyle: CSSProperties | undefined = mirrored ? {transform: 'scaleX(-1)', ...style} : style;
	return (
		<IconNotesPen
			ref={ref}
			size={size ?? 24}
			color={color ?? 'currentColor'}
			stroke={STROKE[weight ?? 'regular']}
			style={mirroredStyle}
			aria-label={alt}
			aria-hidden={alt ? undefined : true}
			{...rest}
		/>
	);
});
ScratchpadIcon.displayName = 'ScratchpadIcon';
